/**
 * A parent gauge — and the "expected" mark that gives it its meaning.
 *
 * Invariant 10: the expected level is **always** drawn. `mastery` puts 50 at
 * "at the programme's level on this date", so a gauge at 55 means nothing
 * without the mark, and a parent left to guess will read it as 55 %.
 *
 * Invariant of §6.3: a competency seen twice is not displayed with the same
 * confidence as one seen thirty times. Low observation counts dim the bar and
 * say so in words — an attenuated bar alone would just look like a design
 * choice.
 *
 * And **zero observations is not a low count, it is no measurement at all**. A
 * bar drawn at the expected mark reading "au niveau attendu" is a claim; on the
 * evening the referential lands and nothing has been worked yet, every gauge
 * would make it about every competency at once. So at zero the gauge states its
 * own emptiness and asserts nothing.
 *
 * What the gauge *says* is decided by `reading()` — a pure function, so the
 * rule above is testable without pulling a DOM into this project.
 */

const MIN_CONFIDENT_OBSERVATIONS = 5;

/** Above or below the expected mark — or nothing at all, which is a valid answer. */
export type Trend = "above" | "at" | "below" | "none";

export type GaugeReading = {
  trend: Trend;
  trendLabel: string;
  trendGlyph: string;
  /** The line under the track. Never a percentage. */
  foot: string;
  /** False when nothing was measured: no bar is drawn at all. */
  showFill: boolean;
  uncertain: boolean;
  unmeasured: boolean;
  width: number;
};

export function reading({
  score,
  expected,
  observations,
  sublabel,
}: {
  score: number;
  expected: number;
  observations: number;
  sublabel?: string;
}): GaugeReading {
  const width = Math.max(0, Math.min(100, score));
  const unmeasured = observations === 0;
  const uncertain = observations < MIN_CONFIDENT_OBSERVATIONS;
  const gap = width - expected;
  const trend: Trend = unmeasured ? "none" : gap > 5 ? "above" : gap < -5 ? "below" : "at";

  const trendLabel = {
    none: "pas encore mesuré",
    above: "au-dessus de l'attendu",
    below: "sous l'attendu",
    at: "au niveau attendu",
  }[trend];

  const foot = unmeasured
    ? "aucune observation pour l'instant"
    : uncertain
      ? `peu observé — ${observations} observation${observations > 1 ? "s" : ""}, à lire avec prudence`
      : (sublabel ?? `${observations} observations`);

  return {
    trend,
    trendLabel,
    trendGlyph: { above: "▲", below: "▼", none: "·", at: "≈" }[trend],
    foot,
    showFill: !unmeasured,
    uncertain,
    unmeasured,
    width,
  };
}

export function Gauge({
  label,
  name,
  score,
  expected = 50,
  observations,
  sublabel,
}: {
  /** Left of the head row. Omit when the caller already names the thing above
      the gauge — repeating the state next to its own chip says it twice. */
  label?: string;
  /** What the gauge is about, for screen readers, when `label` is omitted. */
  name?: string;
  score: number;
  expected?: number;
  observations: number;
  sublabel?: string;
}) {
  const view = reading({ score, expected, observations, sublabel });

  return (
    <div
      className="gauge"
      data-uncertain={view.uncertain ? "true" : "false"}
      data-unmeasured={view.unmeasured ? "true" : "false"}
    >
      <div className="gauge__head">
        {label ? <span className="gauge__label">{label}</span> : <span />}
        <span className={`gauge__trend gauge__trend--${view.trend}`}>
          <span aria-hidden="true">{view.trendGlyph}</span> {view.trendLabel}
        </span>
      </div>

      <div className="gauge__track" role="img" aria-label={`${name ?? label ?? ""} : ${view.trendLabel}`}>
        {/* Nothing is drawn where nothing was measured: a bar at the expected
            mark would be read as "on track", which is a claim, not a blank. */}
        {view.showFill ? <div className="gauge__fill" style={{ width: `${view.width}%` }} /> : null}
        {/* Always drawn, never conditional — invariant 10. */}
        <div className="gauge__expected" style={{ left: `${expected}%` }}>
          <span className="gauge__expected-label">attendu</span>
        </div>
      </div>

      <p className="gauge__foot">{view.foot}</p>
    </div>
  );
}
